import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { createContext, useContext, useMemo, useRef } from 'react';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { captureException } from '@atlaskit/linking-common/sentry';
import { ANALYTICS_CHANNEL } from '../../constants';
import createEventPayload from '../../utils/analytics/analytics.codegen';
import { getErrorType, getNetworkFields } from '../../utils/errors';
var ExperienceContext = /*#__PURE__*/createContext({
  success: function success() {},
  failure: function failure() {}
});

/**
 * Error message matches to ignore
 * These should not affect our SLOs as there's nothing we can do about them
 */
var IGNORE_ERROR_MESSAGES = [/failed to fetch/i];

/**
 * Returns false for errors that should not be considered failures of our SLO
 * because they are failures only the user can handle.
 */
var isErrorSLOFailure = function isErrorSLOFailure(error) {
  if (error instanceof Error) {
    if (IGNORE_ERROR_MESSAGES.some(function (msg) {
      return msg.test(error.message);
    })) {
      return false;
    }
  }
  return true;
};

/**
 * Experience provider that simply keeps track of the state of the experience.
 * Fires an operational event when experience state changes to FAILED.
 */
export var Experience = function Experience(_ref) {
  var children = _ref.children;
  var _useAnalyticsEvents = useAnalyticsEvents(),
    createAnalyticsEvent = _useAnalyticsEvents.createAnalyticsEvent;
  var experience = useRef('STARTED');
  var value = useMemo(function () {
    return {
      success: function success() {
        if (experience.current !== 'SUCCEEDED') {
          experience.current = 'SUCCEEDED';
        }
      },
      /**
       * Indicate the experience has failed and capture exception information
       * @param error Typically an Error class or Response class
       */
      failure: function failure(error) {
        var experienceStatus = 'FAILED';

        /**
         * Always capture an event to Splunk
         */
        createAnalyticsEvent(createEventPayload('operational.linkCreateExperience.failed', _objectSpread({
          /**
           * The type of error that has failed the experience
           */
          errorType: getErrorType(error),
          /**
           * Error message if instanceof Error
           */
          errorMessage: error instanceof Error ? error.message : null,
          /**
           * The current status of the experience (has failed)
           */
          experienceStatus: experienceStatus,
          /**
           * Previous experience status indicates whether the experience
           * has just failed now, or has already failing
           */
          previousExperienceStatus: experience.current,
          /**
           * Whether the failure should be involved when considering SLI/SLO
           */
          isSLOFailure: isErrorSLOFailure(error)
        }, getNetworkFields(error)))).fire(ANALYTICS_CHANNEL);
        if (error instanceof Error) {
          // Capture exception to Sentry
          captureException(error, 'link-create');
        }

        /**
         * Only consider the experience truly failed if the
         * failure is one we haven't correctly handled.
         *
         * In otherwords allow the experience to be "restarted" for the user to try again
         */
        if (isErrorSLOFailure(error)) {
          if (experience.current !== experienceStatus) {
            experience.current = experienceStatus;
          }
        }
      }
    };
  }, [experience, createAnalyticsEvent]);
  return /*#__PURE__*/React.createElement(ExperienceContext.Provider, {
    value: value
  }, children);
};
export var useExperience = function useExperience() {
  return useContext(ExperienceContext);
};